import { inject, Injectable, Injector, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { AuthState } from '@wilson/auth/core';
import {
  Activity,
  ActivityCategoriesIdsMap,
  GeoLocation,
  OrganizationalUnit,
  Shift,
  TimelineShiftWithActivities,
  UnassignedShift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import { ShiftMultiSelectHelperService } from '@wilson/shift-timeline/assignments-gantt-chart';
import { TimelineCommonOperationsService } from '../../../services/timeline-common-operations.service';
import {
  CreateNewViaDragAndDropService,
  generateDummyShift,
  getDateFromPixelPosition,
} from '@wilson/shift-timeline/services';
import {
  AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  AddShiftWithoutActivitiesToUnassignedRegion,
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  ShiftTimelineDataState,
  ShiftTimelineDataTransformerService,
  ShiftTimelineState,
  ShiftTimelineUnassignedRegionState,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { addHours, startOfMinute } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';

export type UnassignedRegionView = 'regular' | 'cellView';

@Injectable()
export class NewShiftToUnassignedRegionDropHelperService {
  private readonly store = inject(Store);
  private readonly locationsStoreService = inject(LocationsStoreService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );

  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly translateService = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly timelineCommonOperationService = inject(
    TimelineCommonOperationsService,
  );
  private readonly injector = inject(Injector);
  readonly timelineViewSignal = signal<UnassignedRegionView>('regular');

  public createShiftFromRegularViewDrop(
    rawOffsetX: number,
    dragStartOffsetX: number | null,
  ): Promise<void> {
    this.timelineViewSignal.set('regular');
    return this.createShiftFromContext(rawOffsetX, dragStartOffsetX);
  }

  public createShiftFromCellViewDrop(
    rawOffsetX: number,
    dragStartOffsetX: number | null,
  ): Promise<void> {
    this.timelineViewSignal.set('cellView');
    return this.createShiftFromContext(rawOffsetX, dragStartOffsetX);
  }

  private getBoxLeftBorderPositionInPixel(
    rawOffsetX: number,
    dragStartOffsetX: number | null,
  ): number {
    return rawOffsetX - (dragStartOffsetX ?? 0);
  }

  private async resolveOrganizationalUnitAndLocation(): Promise<{
    organizationalUnit: OrganizationalUnit;
    location: GeoLocation;
  } | null> {
    const organizationalUnit = this.store.selectSnapshot(
      AuthState.organizationalUnit,
    );

    const location = organizationalUnit?.locationId
      ? await firstValueFrom(
          this.locationsStoreService.getLocationFromCache(
            organizationalUnit.locationId as string,
          ),
        )
      : null;

    if (!organizationalUnit?.locationId || !location) {
      this.nzMessageService.error(
        this.translateService.instant('general.something_went_wrong'),
      );
      return null;
    }
    return { organizationalUnit, location };
  }

  private readonly actionsByView = {
    regular: {
      remove: (shiftId: string) =>
        new RemoveShiftWithoutActivitiesFromUnassignedRegion({ shiftId }),
      add: (shift: UnassignedShift) =>
        new AddShiftWithoutActivitiesToUnassignedRegion(shift),
    },
    cellView: {
      remove: (shiftId: string) =>
        new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
          shiftId,
        }),
      add: (shift: UnassignedShift) =>
        new AddShiftWithoutActivitiesToCellViewUnassignedRegion(shift),
    },
  } as const;

  private upsertUnassignedMockShiftToState(
    mockShift: TimelineShiftWithActivities,
  ): UnassignedShift {
    const mockShiftWithoutActivities =
      this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
        mockShift,
      );

    const mockUnassignedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        mockShiftWithoutActivities,
      );

    this.store.dispatch(
      this.actionsByView[this.timelineViewSignal()].add(mockUnassignedShift),
    );
    return mockUnassignedShift;
  }
  private async createShiftFromContext(
    rawOffsetX: number,
    dragStartOffsetX: number | null,
  ): Promise<void> {
    const resolved = await this.resolveOrganizationalUnitAndLocation();

    if (resolved) {
      const { organizationalUnit, location } = resolved;
      this.createNewViaDragAndDropService.setQuickShiftDraggable(false);
      const boxLeftEdgeOffsetX = this.getBoxLeftBorderPositionInPixel(
        rawOffsetX,
        dragStartOffsetX,
      );

      const shiftStartDate = getDateFromPixelPosition({
        pixelPosition: boxLeftEdgeOffsetX,
        timeframe: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        frameLength: this.store.selectSnapshot(ShiftTimelineState.frameLength),
      });
      const shiftEndDate = startOfMinute(addHours(shiftStartDate, 8));

      const mockShift = generateDummyShift({
        orgUnit: organizationalUnit,
        location,
        shiftStartDate: shiftStartDate,
        shiftEndDate: shiftEndDate,
        userId: null,
        activityCategoryId: ActivityCategoriesIdsMap.More,
        injector: this.injector,
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { projectId, ...shiftWithoutProjectId } = mockShift;

      // NOTE: mock for optimistic visual displaying of shift, removed on BE success/error
      this.upsertUnassignedMockShiftToState(mockShift);

      try {
        // NOTE: real shift creation after the backend call
        const unassignedShift = await this.createRealShiftAndSwap(
          shiftWithoutProjectId,
        );
        const isExpanded = this.store.selectSnapshot(
          ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
        );
        if (!isExpanded) {
          this.timelineCommonOperationService.refreshUnassignedRegion();
        }

        this.createNewViaDragAndDropService.setQuickShiftDraggable(true);

        this.shiftMultiSelectHelperService.setSelectedShift([
          {
            id: unassignedShift.id,
            left: null,
            right: null,
            isUnAssignedShift: true,
          },
        ]);
      } catch (error) {
        this.store.dispatch(
          this.actionsByView[this.timelineViewSignal()].remove(
            CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
          ),
        );
        this.createNewViaDragAndDropService.setQuickShiftDraggable(true);

        captureException(error);
        this.nzMessageService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
      }
    }
  }

  private async createRealShiftAndSwap(
    shiftWithoutProjectId: Shift,
  ): Promise<UnassignedShift> {
    const materializedShifts = await firstValueFrom(
      this.shiftsService.createShifts([shiftWithoutProjectId]),
    );
    const resolvedShifts = await firstValueFrom(
      this.shiftsService.getResolvedShift(materializedShifts[0].id as string),
    );
    const resolvedShift = resolvedShifts[0] as Shift &
      WithPreparedAttributes & { id: string; activities: Activity[] };

    const shiftWithoutActivities =
      this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
        resolvedShift,
      );
    const unassignedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        shiftWithoutActivities,
      );
    this.store.dispatch([
      this.actionsByView[this.timelineViewSignal()].remove(
        CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
      ),
      this.actionsByView[this.timelineViewSignal()].add(unassignedShift),
    ]);

    return unassignedShift;
  }
}
